import { RentAmendmentDialog } from '@features/amendments/components/RentAmendmentDialog'
import { PRICE_FIELDS, type Prices } from '@services/amendments/types'
import { useExtendContract } from '@services/contracts/mutations/useExtendContract'
import { useContracts } from '@services/contracts/queires/useContracts'
import { useOffersDetail } from '@services/offers/queries/useOffersDetail'
import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  format,
  parseISO
} from 'date-fns'
import { CalendarPlus, Info } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

// Notice period of the renewal option in art. II(2) of the client contract.
const NOTICE_DAYS = 14

/**
 * Lease renewal for an ONGOING offer: moves the end date of the signed
 * contract (all signer rows, on the backend) and optionally emails the
 * clients the renewal notice the contract's option requires. The new date
 * defaults to one year after the current end, as the option says.
 */
export function ExtendContractDialog(props: { offerId: string }) {
  const { offerId } = props
  const { t } = useTranslation('offers')
  const [open, setOpen] = useState(false)
  const [endDate, setEndDate] = useState<string | undefined>()
  const [notify, setNotify] = useState(true)
  const [changeRent, setChangeRent] = useState(false)
  const [amendmentMonth, setAmendmentMonth] = useState<string | undefined>()
  const { data: offer } = useOffersDetail<'ADMIN'>(offerId)
  const { data: contracts } = useContracts({
    params: { offer: offerId, type: 'offer' }
  })
  const extendContract = useExtendContract()

  const contract = contracts?.results?.find((c) => c.status === 'completed')
  if (!contract?.id || !contract.end_date) return null

  const currentEnd = parseISO(contract.end_date)
  const daysLeft = differenceInCalendarDays(currentEnd, new Date())
  const minDate = format(addDays(currentEnd, 1), 'yyyy-MM-dd')

  // A rent amendment always starts on the 1st: the first whole month of the
  // new period — the month after the old end, or the one after that when the
  // new period starts mid-month.
  const newPeriodStart = addDays(currentEnd, 1)
  const firstAmendmentMonth = format(
    newPeriodStart.getDate() === 1
      ? newPeriodStart
      : addMonths(newPeriodStart, 1),
    'yyyy-MM'
  )

  const current = Object.fromEntries(
    PRICE_FIELDS.map((f) => [f, Number(offer?.[f] ?? 0)])
  ) as Prices

  const openDialog = () => {
    setEndDate(format(addYears(currentEnd, 1), 'yyyy-MM-dd'))
    setNotify(true)
    setChangeRent(false)
    setOpen(true)
  }

  const extend = () => {
    if (!endDate) return
    // The renewal notice promises "the same terms" — it must not go out
    // together with a rent increase.
    const sendNotice = notify && !changeRent
    extendContract.mutate(
      { id: contract.id!, body: { end_date: endDate, notify: sendNotice } },
      {
        onSuccess: () => {
          setOpen(false)
          if (changeRent) setAmendmentMonth(firstAmendmentMonth)
        }
      }
    )
  }

  return (
    <>
      <Button variant="outline" onClick={openDialog}>
        <CalendarPlus className="h-4 w-4" />
        {t('admin.actions.extend_contract')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <CalendarPlus className="h-5 w-5" />
              </span>
              <div>
                <DialogTitle>
                  {t('admin.actions.extend_dialog_title')}
                </DialogTitle>
                <p className="m-0 mt-1 text-sm text-muted-foreground">
                  {t('admin.actions.extend_dialog_hint')}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">
                {t('admin.actions.extend_current_end')}
              </span>
              <span className="font-semibold text-ink">
                {format(currentEnd, 'dd.MM.yyyy')}
              </span>
            </div>

            <div className="space-y-1.5">
              <Label>{t('admin.actions.extend_new_end')}</Label>
              <DatePicker value={endDate} onChange={setEndDate} min={minDate} />
            </div>

            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={changeRent}
                onCheckedChange={(checked) => setChangeRent(checked === true)}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium text-ink">
                  {t('admin.actions.extend_change_rent')}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('admin.actions.extend_change_rent_hint', {
                    month: firstAmendmentMonth.split('-').reverse().join('/'),
                    interpolation: { escapeValue: false }
                  })}
                </span>
              </span>
            </label>

            <label
              className={cn(
                'flex cursor-pointer items-start gap-3',
                changeRent && 'pointer-events-none opacity-50'
              )}
            >
              <Checkbox
                disabled={changeRent}
                checked={notify && !changeRent}
                onCheckedChange={(checked) => setNotify(checked === true)}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium text-ink">
                  {t('admin.actions.extend_notify')}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('admin.actions.extend_notify_hint')}
                </span>
              </span>
            </label>

            {daysLeft < NOTICE_DAYS && (
              <div className="flex items-start gap-2 rounded-xl bg-peach/15 px-3 py-2 text-sm text-ink">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-peach" />
                {t('admin.actions.extend_late_warning')}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghostDark" onClick={() => setOpen(false)}>
              {t('admin.form.no')}
            </Button>
            <Button
              onClick={extend}
              disabled={!endDate || extendContract.isPending}
            >
              {t('admin.actions.extend_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RentAmendmentDialog
        side="TENANT"
        offerId={offerId}
        current={current}
        initialMonth={amendmentMonth}
        open={!!amendmentMonth}
        onOpenChange={(value) => {
          if (!value) setAmendmentMonth(undefined)
        }}
      />
    </>
  )
}
